import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { PenToolIcon, RotateCcwIcon, CheckIcon } from 'lucide-react';
interface SignatureScreenProps {
  firstName: string;
  lastName: string;
  onComplete: (signatureDataUrl: string) => void;
}
export function SignatureScreen({
  firstName,
  lastName,
  onComplete
}: SignatureScreenProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [date] = useState(new Date().toLocaleDateString('de-DE'));
  // Handle resizing of canvas to ensure crisp lines on high DPI screens
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Save current content if any
      const dataUrl = canvas.toDataURL();
      canvas.width = parent.clientWidth;
      canvas.height = 200; // Fixed height
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = '#1a1a2e';
        // Restore content after resize if it existed
        if (hasSignature) {
          const img = new Image();
          img.src = dataUrl;
          img.onload = () => ctx.drawImage(img, 0, 0);
        }
      }
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [hasSignature]);
  const getCoordinates = (e: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return {
      x: 0,
      y: 0
    };
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };
  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const {
      x,
      y
    } = getCoordinates(e);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    }
  };
  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;
    const {
      x,
      y
    } = getCoordinates(e);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
      setHasSignature(true);
    }
  };
  const stopDrawing = () => {
    if (isDrawing) {
      const ctx = canvasRef.current?.getContext('2d');
      ctx?.closePath();
      setIsDrawing(false);
    }
  };
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
    }
  };
  const handleSubmit = () => {
    if (hasSignature && canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      onComplete(dataUrl);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="min-h-screen bg-[#f8f9fa] py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center" data-id="element-282">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100" data-id="element-283">
        <div className="bg-[#1a1a2e] px-8 py-6 text-white flex items-center" data-id="element-284">
          <PenToolIcon className="h-6 w-6 mr-3 text-[#8DC63F]" data-id="element-285" />
          <h2 className="text-2xl font-bold" data-id="element-286">Bestätigung & Unterschrift</h2>
        </div>

        <div className="p-8" data-id="element-287">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 mb-8 text-[#4a4a5a] text-sm md:text-base leading-relaxed" data-id="element-288">
            <p data-id="element-289">
              Hiermit bestätige ich,{' '}
              <strong data-id="element-290">
                {firstName} {lastName}
              </strong>
              , dass ich alle Inhalte der Schulung vollständig gelesen und
              verstanden habe. Ich verpflichte mich, die beschriebenen
              Schutzmaßnahmen einzuhalten.
            </p>
            <p className="mt-4 text-gray-500" data-id="element-291">Datum: {date}</p>
          </div>

          <div className="mb-6" data-id="element-292">
            <div className="flex justify-between items-end mb-2" data-id="element-293">
              <label className="block text-sm font-medium text-[#1a1a2e]" data-id="element-294">
                Ihre Unterschrift
              </label>
              <button onClick={clearSignature} className="text-xs text-gray-500 hover:text-[#1a1a2e] flex items-center transition-colors" type="button" data-id="element-295">
                <RotateCcwIcon className="h-3 w-3 mr-1" data-id="element-296" />
                Neu zeichnen
              </button>
            </div>

            <div className="w-full border-2 border-dashed border-gray-300 rounded-lg bg-white relative overflow-hidden touch-none" data-id="element-297">
              {!hasSignature && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-298">
                  <span className="text-gray-300 text-lg select-none" data-id="element-299">
                    Hier unterschreiben
                  </span>
                </div>}
              <canvas ref={canvasRef} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} className="w-full cursor-crosshair block" style={{
              touchAction: 'none'
            }} data-id="element-300" />
            </div>
          </div>

          <button onClick={handleSubmit} disabled={!hasSignature} className={`w-full flex items-center justify-center py-4 px-4 border border-transparent rounded-lg shadow-sm text-white transition-all font-medium text-lg
              ${hasSignature ? 'bg-[#8DC63F] hover:bg-[#7ab036] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8DC63F]' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-301">
            <CheckIcon className="mr-2 h-5 w-5" data-id="element-302" />
            Schulung abschließen
          </button>
        </div>
      </div>
    </motion.div>;
}